/**
 * BAN search (`api-adresse.data.gouv.fr`) and Nest `GET /departments`.
 */

import apiClient from './api-client';
import type { GeographySuggestion, Department } from '@/types/geography.types';

/** GeoJSON feature from BAN `GET /search`. */
interface BanGeographyFeature {
  properties: {
    label: string;
    context?: string;
    postcode?: string;
    city?: string;
    depcode?: string;
  };
  geometry: {
    coordinates: [number, number];
  };
}

/** BAN `GET /search/` payload. */
interface BanGeographyResponse {
  features: BanGeographyFeature[];
}

/** Public BAN address-search endpoint. */
const BAN_SEARCH_URL = 'https://api-adresse.data.gouv.fr/search/';

/** `GET /search/?q=&limit=&autocomplete=1` — maps BAN features to suggestions. */
export async function searchGeography(
  query: string,
  options?: { limit?: number; signal?: AbortSignal },
): Promise<GeographySuggestion[]> {
  const params = new URLSearchParams({
    q: query,
    limit: String(options?.limit ?? 5),
    autocomplete: '1',
  });
  try {
    const response = await fetch(`${BAN_SEARCH_URL}?${params}`, {
      signal: options?.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error('geographySearchFailed');
    }
    const data = (await response.json()) as BanGeographyResponse;
    return (data.features ?? []).map((feature) => ({
      label: feature.properties.label,
      value: feature.properties.label,
      context: feature.properties.context,
      postalCode: feature.properties.postcode,
      city: feature.properties.city,
      departmentCode: feature.properties.depcode,
      coordinates: {
        lng: feature.geometry.coordinates[0],
        lat: feature.geometry.coordinates[1],
      },
    }));
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') throw err;
    throw new Error('geographySearchFailed');
  }
}

/** `GET /departments` — catalogue for a country (default `FR`). */
export const getDepartments = async (country: string = 'FR'): Promise<Department[]> => {
  const { data } = await apiClient.get<Department[]>('/departments', {
    params: { country },
  });
  return data;
};
